import type { ProjectScope } from './project-scope';

type CostumeCollection = 'pieces' | 'measurements' | 'sizing' | 'fittings' | 'quickChanges';

const pendingCostumeWriteKeys = new Set<string>();
const pendingCostumeDeleteKeys = new Set<string>();

function costumeWriteKey(scope: ProjectScope, collection: CostumeCollection, id: string): string {
    return `${scope.orgId}\0${scope.projectId}\0${collection}\0${id}`;
}

function costumeWritePrefix(scope: ProjectScope, collection: CostumeCollection): string {
    return `${scope.orgId}\0${scope.projectId}\0${collection}\0`;
}

function markPendingCostumeWrite(scope: ProjectScope, collection: CostumeCollection, id: string): void {
    pendingCostumeDeleteKeys.delete(costumeWriteKey(scope, collection, id));
    pendingCostumeWriteKeys.add(costumeWriteKey(scope, collection, id));
}

function markPendingCostumeDelete(scope: ProjectScope, collection: CostumeCollection, id: string): void {
    pendingCostumeWriteKeys.delete(costumeWriteKey(scope, collection, id));
    pendingCostumeDeleteKeys.add(costumeWriteKey(scope, collection, id));
}

function clearPendingCostumeWrite(scope: ProjectScope, collection: CostumeCollection, id: string): void {
    pendingCostumeWriteKeys.delete(costumeWriteKey(scope, collection, id));
}

function isPendingCostumeWrite(scope: ProjectScope, collection: CostumeCollection, id: string): boolean {
    return pendingCostumeWriteKeys.has(costumeWriteKey(scope, collection, id));
}

function isPendingCostumeDelete(scope: ProjectScope, collection: CostumeCollection, id: string): boolean {
    return pendingCostumeDeleteKeys.has(costumeWriteKey(scope, collection, id));
}

function clearAcknowledgedPendingCostumeDeletes(
    scope: ProjectScope,
    collection: CostumeCollection,
    remoteIds: ReadonlySet<string>
): void {
    const prefix = costumeWritePrefix(scope, collection);
    for (const key of Array.from(pendingCostumeDeleteKeys)) {
        if (!key.startsWith(prefix)) continue;
        const id = key.slice(prefix.length);
        if (!remoteIds.has(id)) {
            pendingCostumeDeleteKeys.delete(key);
        }
    }
}

export function markPendingCostumePieceWrite(scope: ProjectScope, id: string): void {
    markPendingCostumeWrite(scope, 'pieces', id);
}

export function markPendingCostumePieceDelete(scope: ProjectScope, id: string): void {
    markPendingCostumeDelete(scope, 'pieces', id);
}

export function clearPendingCostumePieceWrite(scope: ProjectScope, id: string): void {
    clearPendingCostumeWrite(scope, 'pieces', id);
}

export function isPendingCostumePieceWrite(scope: ProjectScope, id: string): boolean {
    return isPendingCostumeWrite(scope, 'pieces', id);
}

export function isPendingCostumePieceDelete(scope: ProjectScope, id: string): boolean {
    return isPendingCostumeDelete(scope, 'pieces', id);
}

export function clearAcknowledgedPendingCostumePieceDeletes(scope: ProjectScope, remoteIds: ReadonlySet<string>): void {
    clearAcknowledgedPendingCostumeDeletes(scope, 'pieces', remoteIds);
}

export function markPendingCostumeMeasurementWrite(scope: ProjectScope, actorId: string): void {
    markPendingCostumeWrite(scope, 'measurements', actorId);
}

export function markPendingCostumeMeasurementDelete(scope: ProjectScope, actorId: string): void {
    markPendingCostumeDelete(scope, 'measurements', actorId);
}

export function clearPendingCostumeMeasurementWrite(scope: ProjectScope, actorId: string): void {
    clearPendingCostumeWrite(scope, 'measurements', actorId);
}

export function isPendingCostumeMeasurementWrite(scope: ProjectScope, actorId: string): boolean {
    return isPendingCostumeWrite(scope, 'measurements', actorId);
}

export function isPendingCostumeMeasurementDelete(scope: ProjectScope, actorId: string): boolean {
    return isPendingCostumeDelete(scope, 'measurements', actorId);
}

export function clearAcknowledgedPendingCostumeMeasurementDeletes(
    scope: ProjectScope,
    remoteIds: ReadonlySet<string>
): void {
    clearAcknowledgedPendingCostumeDeletes(scope, 'measurements', remoteIds);
}

export function markPendingCostumeSizingWrite(scope: ProjectScope, actorId: string): void {
    markPendingCostumeWrite(scope, 'sizing', actorId);
}

export function markPendingCostumeSizingDelete(scope: ProjectScope, actorId: string): void {
    markPendingCostumeDelete(scope, 'sizing', actorId);
}

export function clearPendingCostumeSizingWrite(scope: ProjectScope, actorId: string): void {
    clearPendingCostumeWrite(scope, 'sizing', actorId);
}

export function isPendingCostumeSizingWrite(scope: ProjectScope, actorId: string): boolean {
    return isPendingCostumeWrite(scope, 'sizing', actorId);
}

export function isPendingCostumeSizingDelete(scope: ProjectScope, actorId: string): boolean {
    return isPendingCostumeDelete(scope, 'sizing', actorId);
}

export function clearAcknowledgedPendingCostumeSizingDeletes(
    scope: ProjectScope,
    remoteIds: ReadonlySet<string>
): void {
    clearAcknowledgedPendingCostumeDeletes(scope, 'sizing', remoteIds);
}

export function markPendingCostumeFittingWrite(scope: ProjectScope, id: string): void {
    markPendingCostumeWrite(scope, 'fittings', id);
}

export function markPendingCostumeFittingDelete(scope: ProjectScope, id: string): void {
    markPendingCostumeDelete(scope, 'fittings', id);
}

export function clearPendingCostumeFittingWrite(scope: ProjectScope, id: string): void {
    clearPendingCostumeWrite(scope, 'fittings', id);
}

export function isPendingCostumeFittingWrite(scope: ProjectScope, id: string): boolean {
    return isPendingCostumeWrite(scope, 'fittings', id);
}

export function isPendingCostumeFittingDelete(scope: ProjectScope, id: string): boolean {
    return isPendingCostumeDelete(scope, 'fittings', id);
}

export function clearAcknowledgedPendingCostumeFittingDeletes(scope: ProjectScope, remoteIds: ReadonlySet<string>): void {
    clearAcknowledgedPendingCostumeDeletes(scope, 'fittings', remoteIds);
}

export function markPendingCostumeQuickChangeWrite(scope: ProjectScope, id: string): void {
    markPendingCostumeWrite(scope, 'quickChanges', id);
}

export function markPendingCostumeQuickChangeDelete(scope: ProjectScope, id: string): void {
    markPendingCostumeDelete(scope, 'quickChanges', id);
}

export function clearPendingCostumeQuickChangeWrite(scope: ProjectScope, id: string): void {
    clearPendingCostumeWrite(scope, 'quickChanges', id);
}

export function isPendingCostumeQuickChangeWrite(scope: ProjectScope, id: string): boolean {
    return isPendingCostumeWrite(scope, 'quickChanges', id);
}

export function isPendingCostumeQuickChangeDelete(scope: ProjectScope, id: string): boolean {
    return isPendingCostumeDelete(scope, 'quickChanges', id);
}

export function clearAcknowledgedPendingCostumeQuickChangeDeletes(
    scope: ProjectScope,
    remoteIds: ReadonlySet<string>
): void {
    clearAcknowledgedPendingCostumeDeletes(scope, 'quickChanges', remoteIds);
}

export function clearPendingCostumeWrites(): void {
    pendingCostumeWriteKeys.clear();
    pendingCostumeDeleteKeys.clear();
}
